/**
 * CSRF token plumbing for the cookie-authenticated browser session.
 *
 * The backend (CsrfProtectionFilter) issues a readable `XSRF-TOKEN` cookie and requires state-changing
 * cookie-authenticated requests to echo it in the `X-XSRF-TOKEN` header (HMAC-signed double-submit).
 * This reads that cookie so request wrappers can attach the header. Bearer/patient-token requests are
 * exempt server-side and don't need it.
 */

export const CSRF_HEADER = 'X-XSRF-TOKEN';
const CSRF_COOKIE = 'XSRF-TOKEN';

/** Reads the current CSRF token from the readable cookie, or null if none is present. */
export function getCsrfToken(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/**
 * Returns the CSRF header to merge into a fetch for a given method, or an empty object for safe
 * methods / when no token is available. Centralises the "only on state-changing requests" rule.
 */
export function csrfHeader(method?: string): Record<string, string> {
  const m = (method ?? 'GET').toUpperCase();
  if (SAFE_METHODS.has(m)) return {};
  const token = getCsrfToken();
  return token ? { [CSRF_HEADER]: token } : {};
}

// The cookie is named after the de-facto convention (XSRF-TOKEN / X-XSRF-TOKEN); exported for reuse.
export { CSRF_COOKIE };

/**
 * The patient portal's token (medopd-docs 107): the server issues it as the readable `XSRF-PATIENT` cookie, bound to
 * the patient account, and checks it on cookie-authenticated patient writes. A separate cookie, so a browser that is
 * signed in as staff and as a patient keeps both working.
 */
export function patientCsrfHeader(method?: string): Record<string, string> {
  const m = (method ?? 'GET').toUpperCase();
  if (SAFE_METHODS.has(m)) return {};
  try {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-PATIENT=([^;]+)/);
    return match ? { [CSRF_HEADER]: decodeURIComponent(match[1]) } : {};
  } catch {
    return {};
  }
}
